import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { adminAPI, settingsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShieldCheck, Users, Settings, Plus, UserCheck, UserX, Trash2, Save, ScrollText, User, FileText, Stethoscope, LogIn, Archive, Eye, EyeOff, Pencil, X, Upload, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = { admin: 'Administrator', hauptmitarbeiter: 'Hauptmitarbeiter', mitarbeiter: 'Mitarbeiter' };
const ROLE_COLORS = { admin: 'bg-violet-50 text-violet-700 border-violet-200', hauptmitarbeiter: 'bg-sky-50 text-sky-700 border-sky-200', mitarbeiter: 'bg-slate-100 text-slate-600 border-slate-200' };

const ACTION_ICONS = {
  login: LogIn, register: UserCheck, create: Plus, update: Save, delete: Trash2, archive: Archive, restore: Archive,
};
const ACTION_LABELS = {
  login: 'Anmeldung', register: 'Registrierung', create: 'Erstellt', update: 'Geändert', delete: 'Gelöscht', archive: 'Archiviert', restore: 'Wiederhergestellt',
};
const ENTITY_LABELS = {
  user: 'Benutzer', patient: 'Patient', wound: 'Wunde', wound_report: 'Wundbericht', settings: 'Einstellungen',
};
const ENTITY_ICONS = {
  user: User, patient: Users, wound: Stethoscope, wound_report: FileText, settings: Settings,
};

export default function AdminPage() {
  const { user, isAdmin, settings, loadSettings } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'mitarbeiter' });
  const [showNewUserPw, setShowNewUserPw] = useState(false);
  const [tourenEnabled, setTourenEnabled] = useState(settings.tourenplanung_enabled !== false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState('');
  
  // Edit user state
  const [editUser, setEditUser] = useState(null);
  const [editData, setEditData] = useState({ name: '', email: '', password: '', role: '' });
  const [showEditPw, setShowEditPw] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  
  // CSV Import state
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    loadUsers();
  }, [isAdmin]);

  useEffect(() => { setTourenEnabled(settings.tourenplanung_enabled !== false); }, [settings]);

  const loadUsers = async () => {
    try {
      const res = await adminAPI.users();
      setUsers(res.data);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const loadAuditLogs = async (entityType = '') => {
    setAuditLoading(true);
    try {
      const params = { limit: 200 };
      if (entityType) params.entity_type = entityType;
      const res = await adminAPI.auditLog(params);
      setAuditLogs(res.data);
    } catch { toast.error('Fehler beim Laden des Audit-Logs'); }
    finally { setAuditLoading(false); }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) { toast.error('Alle Felder ausfüllen'); return; }
    setSaving(true);
    try {
      await adminAPI.createUser(newUser);
      toast.success('Benutzer erstellt');
      setShowCreate(false);
      setNewUser({ name: '', email: '', password: '', role: 'mitarbeiter' });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Erstellen'); }
    finally { setSaving(false); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await adminAPI.updateUser(userId, { role });
      toast.success('Rolle aktualisiert');
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await adminAPI.updateUser(userId, { active: !currentActive });
      toast.success(currentActive ? 'Benutzer deaktiviert' : 'Benutzer aktiviert');
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Benutzer "${userName}" wirklich löschen?`)) return;
    try {
      await adminAPI.deleteUser(userId);
      toast.success('Benutzer gelöscht');
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditData({ name: u.name, email: u.email, password: '', role: u.role });
    setShowEditPw(false);
  };

  const closeEditUser = () => {
    setEditUser(null);
    setEditData({ name: '', email: '', password: '', role: '' });
  };

  const handleSaveEditUser = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      const updatePayload = {};
      if (editData.name !== editUser.name) updatePayload.name = editData.name;
      if (editData.email !== editUser.email) updatePayload.email = editData.email;
      if (editData.role !== editUser.role) updatePayload.role = editData.role;
      if (editData.password && editData.password.length > 0) updatePayload.password = editData.password;
      
      if (Object.keys(updatePayload).length === 0) {
        toast.info('Keine Änderungen');
        closeEditUser();
        return;
      }
      
      await adminAPI.updateUser(editUser.id, updatePayload);
      toast.success('Benutzer aktualisiert');
      closeEditUser();
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setEditSaving(false); }
  };

  // CSV Import functions
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Bitte nur CSV-Dateien auswählen');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Bitte CSV-Datei auswählen');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const res = await adminAPI.importPatients(importFile);
      setImportResult(res.data);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Import');
      setImportResult({ errors: [e.response?.data?.detail || 'Unbekannter Fehler'] });
    } finally {
      setImporting(false);
    }
  };

  const closeImport = () => {
    setShowImport(false);
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTourenToggle = async (enabled) => {
    setTourenEnabled(enabled);
    try {
      await settingsAPI.update({ tourenplanung_enabled: enabled });
      await loadSettings();
      toast.success(enabled ? 'Tourenplanung aktiviert' : 'Tourenplanung deaktiviert');
    } catch { toast.error('Fehler beim Speichern'); setTourenEnabled(!enabled); }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-3xl mx-auto space-y-4" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-violet-700" />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 tracking-tight">Verwaltung</h1>
          <p className="text-sm text-slate-500">Benutzer, Einstellungen & Protokoll</p>
        </div>
      </div>

      <Tabs defaultValue="users" className="w-full" onValueChange={(v) => { if (v === 'audit') loadAuditLogs(auditFilter); }}>
        <TabsList className="w-full bg-slate-100 rounded-xl p-1 h-auto">
          <TabsTrigger value="users" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-users">
            <Users className="w-4 h-4 mr-1.5" />Benutzer
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-audit">
            <ScrollText className="w-4 h-4 mr-1.5" />Protokoll
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium" data-testid="tab-settings">
            <Settings className="w-4 h-4 mr-1.5" />Einstellungen
          </TabsTrigger>
        </TabsList>

        {/* ── Users Tab ── */}
        <TabsContent value="users" className="mt-4 space-y-3">
          <Button onClick={() => setShowCreate(!showCreate)} className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-2" />Neuen Benutzer anlegen
          </Button>

          {showCreate && (
            <Card className="border border-teal-200 shadow-sm animate-fade-in">
              <CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Name</Label><Input value={newUser.name} onChange={e => setNewUser(p => ({...p, name: e.target.value}))} placeholder="Vor- und Nachname" className="h-11" data-testid="new-user-name" /></div>
                  <div className="space-y-1"><Label>E-Mail</Label><Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({...p, email: e.target.value}))} placeholder="name@beispiel.de" className="h-11" data-testid="new-user-email" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Passwort</Label>
                    <div className="relative">
                      <Input type={showNewUserPw ? 'text' : 'password'} value={newUser.password} onChange={e => setNewUser(p => ({...p, password: e.target.value}))} placeholder="Min. 8 Zeichen, Aa1" className="h-11 pr-12" data-testid="new-user-password" />
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" onClick={() => setShowNewUserPw(!showNewUserPw)} tabIndex={-1}>
                        {showNewUserPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1"><Label>Rolle</Label>
                    <Select value={newUser.role} onValueChange={v => setNewUser(p => ({...p, role: v}))}>
                      <SelectTrigger className="h-11" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mitarbeiter">Mitarbeiter</SelectItem>
                        <SelectItem value="hauptmitarbeiter">Hauptmitarbeiter</SelectItem>
                        <SelectItem value="admin">Administrator</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <Button onClick={handleCreateUser} disabled={saving} className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="submit-create-user">
                  <Save className="w-4 h-4 mr-2" />{saving ? 'Erstellen...' : 'Benutzer erstellen'}
                </Button>
              </CardContent>
            </Card>
          )}

          {loading ? <div className="h-20 bg-slate-100 rounded-2xl animate-pulse" /> :
            users.map(u => (
              <Card key={u.id} className={`border shadow-sm ${!u.active ? 'opacity-60 border-slate-300' : 'border-slate-200'}`} data-testid={`user-card-${u.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${u.active !== false ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-500'}`}>
                      {u.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 truncate">{u.name}</p>
                        <Badge variant="outline" className={`text-[10px] border ${ROLE_COLORS[u.role] || ROLE_COLORS.mitarbeiter}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                        {u.active === false && <Badge variant="outline" className="text-[10px] border bg-rose-50 text-rose-600 border-rose-200">Deaktiviert</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{u.email}</p>
                    </div>
                    {u.id !== user?.id && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditUser(u)} className="h-8 w-8 p-0" data-testid={`edit-user-${u.id}`} title="Bearbeiten">
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Select value={u.role} onValueChange={v => handleRoleChange(u.id, v)}>
                          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mitarbeiter">Mitarbeiter</SelectItem>
                            <SelectItem value="hauptmitarbeiter">Hauptmitarbeiter</SelectItem>
                            <SelectItem value="admin">Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u.id, u.active !== false)} className="h-8 w-8 p-0" data-testid={`toggle-active-${u.id}`} title={u.active !== false ? 'Deaktivieren' : 'Aktivieren'}>
                          {u.active !== false ? <UserX className="w-4 h-4 text-amber-600" /> : <UserCheck className="w-4 h-4 text-emerald-600" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id, u.name)} className="h-8 w-8 p-0" data-testid={`delete-user-${u.id}`} title="Löschen">
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          }

          {/* Edit User Modal */}
          {editUser && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeEditUser}>
              <Card className="w-full max-w-md border shadow-xl animate-fade-in" onClick={e => e.stopPropagation()}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <Pencil className="w-5 h-5 text-teal-600" />
                      Benutzer bearbeiten
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={closeEditUser} className="h-8 w-8 p-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 pt-2">
                  <div className="space-y-1">
                    <Label>Name</Label>
                    <Input 
                      value={editData.name} 
                      onChange={e => setEditData(p => ({...p, name: e.target.value}))} 
                      className="h-11" 
                      data-testid="edit-user-name" 
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>E-Mail</Label>
                    <Input 
                      type="email"
                      value={editData.email} 
                      onChange={e => setEditData(p => ({...p, email: e.target.value}))} 
                      className="h-11" 
                      data-testid="edit-user-email" 
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Rolle</Label>
                    <Select value={editData.role} onValueChange={v => setEditData(p => ({...p, role: v}))}>
                      <SelectTrigger className="h-11" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mitarbeiter">Mitarbeiter</SelectItem>
                        <SelectItem value="hauptmitarbeiter">Hauptmitarbeiter</SelectItem>
                        <SelectItem value="admin">Administrator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Neues Passwort <span className="text-slate-400 text-xs">(leer lassen = unverändert)</span></Label>
                    <div className="relative">
                      <Input 
                        type={showEditPw ? 'text' : 'password'}
                        value={editData.password} 
                        onChange={e => setEditData(p => ({...p, password: e.target.value}))} 
                        placeholder="Min. 8 Zeichen" 
                        className="h-11 pr-12" 
                        data-testid="edit-user-password" 
                      />
                      <button 
                        type="button" 
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" 
                        onClick={() => setShowEditPw(!showEditPw)} 
                        tabIndex={-1}
                      >
                        {showEditPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={closeEditUser} className="flex-1 h-11 rounded-xl">
                      Abbrechen
                    </Button>
                    <Button 
                      onClick={handleSaveEditUser} 
                      disabled={editSaving} 
                      className="flex-1 h-11 bg-teal-700 hover:bg-teal-800 text-white rounded-xl"
                      data-testid="save-edit-user"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editSaving ? 'Speichern...' : 'Speichern'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ── Audit Log Tab ── */}
        <TabsContent value="audit" className="mt-4 space-y-3">
          <div className="flex gap-2">
            <Select value={auditFilter} onValueChange={(v) => { setAuditFilter(v); loadAuditLogs(v); }}>
              <SelectTrigger className="h-10 flex-1" data-testid="audit-filter"><SelectValue placeholder="Alle Aktionen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">Alle Aktionen</SelectItem>
                <SelectItem value="user">Benutzer</SelectItem>
                <SelectItem value="patient">Patienten</SelectItem>
                <SelectItem value="wound">Wunden</SelectItem>
                <SelectItem value="wound_report">Berichte</SelectItem>
                <SelectItem value="settings">Einstellungen</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => loadAuditLogs(auditFilter)} className="h-10 px-4 rounded-xl" data-testid="audit-refresh">
              Aktualisieren
            </Button>
          </div>

          {auditLoading ? (
            <div className="space-y-2">
              {[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">Keine Protokolleinträge</p>
              <p className="text-xs mt-1">Aktionen werden automatisch protokolliert</p>
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="audit-log-list">
              {auditLogs.map((log) => {
                const ActionIcon = ACTION_ICONS[log.action] || FileText;
                const EntityIcon = ENTITY_ICONS[log.entity_type] || FileText;
                const isDestructive = ['delete', 'archive'].includes(log.action);
                return (
                  <Card key={log.id} className="border border-slate-100 shadow-none hover:bg-slate-50/50 transition-colors" data-testid={`audit-entry-${log.id}`}>
                    <CardContent className="p-3 flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isDestructive ? 'bg-rose-50' : 'bg-slate-100'}`}>
                        <ActionIcon className={`w-4 h-4 ${isDestructive ? 'text-rose-500' : 'text-slate-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-medium text-slate-900">{log.user_name}</span>
                          <Badge variant="outline" className={`text-[9px] border py-0 px-1 ${ROLE_COLORS[log.user_role] || ''}`}>
                            {ROLE_LABELS[log.user_role] || log.user_role}
                          </Badge>
                          <span className="text-[10px] text-slate-400">&middot;</span>
                          <span className="text-[10px] text-slate-400">{formatTimestamp(log.timestamp)}</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5">{log.details}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <EntityIcon className="w-3 h-3 text-slate-400" />
                          <span className="text-[10px] text-slate-400">{ENTITY_LABELS[log.entity_type] || log.entity_type}</span>
                          <span className="text-[10px] text-slate-300">&middot;</span>
                          <span className="text-[10px] text-slate-400">{ACTION_LABELS[log.action] || log.action}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Settings Tab ── */}
        <TabsContent value="settings" className="mt-4 space-y-3">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Funktionen</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between" data-testid="tourenplanung-toggle">
                <div>
                  <p className="text-sm font-medium text-slate-900">Tourenplanung</p>
                  <p className="text-xs text-slate-500">Tourenplanung für alle Benutzer freischalten</p>
                </div>
                <Switch checked={tourenEnabled} onCheckedChange={handleTourenToggle} data-testid="tourenplanung-switch" />
              </div>
            </CardContent>
          </Card>

          {/* CSV Import Section */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" />Patienten-Import</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-slate-500">
                CSV-Datei mit Patientendaten hochladen. Bestehende Patienten (Name + Geburtsdatum) werden aktualisiert, neue werden angelegt.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setShowImport(true)} 
                className="w-full h-11 rounded-xl border-dashed border-2"
                data-testid="open-import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                CSV-Datei importieren
              </Button>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Rollen-Übersicht</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                <div key={key} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <Badge variant="outline" className={`text-xs border mb-2 ${ROLE_COLORS[key]}`}>{label}</Badge>
                  <p className="text-xs text-slate-500">
                    {key === 'admin' && 'Vollzugriff: Benutzerverwaltung, Einstellungen, Patienten löschen, Audit-Log, Tourenplanung freischalten'}
                    {key === 'hauptmitarbeiter' && 'Patienten & Wunden erstellen/bearbeiten, archivieren, Tourenplanung nutzen'}
                    {key === 'mitarbeiter' && 'Patienten & Wunden erstellen/bearbeiten/lesen (kein Löschen/Archivieren)'}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CSV Import Modal */}
        {showImport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeImport}>
            <Card className="w-full max-w-lg border shadow-xl animate-fade-in" onClick={e => e.stopPropagation()}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                    Patienten aus CSV importieren
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={closeImport} className="h-8 w-8 p-0">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600 space-y-1">
                  <p className="font-medium">Erwartete CSV-Spalten (Semikolon-getrennt):</p>
                  <p>Nachname, Vorname, Straße, PLZ, Ort, Ortsteil, Telefon, Mobil, Pflegegrad, Geburtstag</p>
                </div>
                
                <div className="space-y-2">
                  <Label>CSV-Datei auswählen</Label>
                  <Input 
                    ref={fileInputRef}
                    type="file" 
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="h-11 cursor-pointer"
                    data-testid="csv-file-input"
                  />
                  {importFile && (
                    <p className="text-xs text-teal-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                {importResult && (
                  <div className={`p-3 rounded-lg text-sm ${importResult.errors?.length > 0 && importResult.imported === 0 ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    <p className="font-medium flex items-center gap-1">
                      {importResult.imported > 0 || importResult.updated > 0 ? (
                        <><CheckCircle className="w-4 h-4" /> Import abgeschlossen</>
                      ) : (
                        <><AlertCircle className="w-4 h-4" /> Import-Ergebnis</>
                      )}
                    </p>
                    <ul className="text-xs mt-1 space-y-0.5">
                      {importResult.imported > 0 && <li>✓ {importResult.imported} neue Patienten importiert</li>}
                      {importResult.updated > 0 && <li>✓ {importResult.updated} Patienten aktualisiert</li>}
                      {importResult.skipped > 0 && <li>○ {importResult.skipped} übersprungen (keine Änderung)</li>}
                      {importResult.errors?.length > 0 && (
                        <li className="text-rose-600">✗ {importResult.errors.length} Fehler</li>
                      )}
                    </ul>
                    {importResult.errors?.length > 0 && importResult.errors.length <= 5 && (
                      <div className="mt-2 text-xs text-rose-600 bg-rose-100 p-2 rounded">
                        {importResult.errors.slice(0, 5).map((e, i) => <p key={i}>{e}</p>)}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={closeImport} className="flex-1 h-11 rounded-xl">
                    {importResult ? 'Schließen' : 'Abbrechen'}
                  </Button>
                  {!importResult && (
                    <Button 
                      onClick={handleImport} 
                      disabled={!importFile || importing} 
                      className="flex-1 h-11 bg-teal-700 hover:bg-teal-800 text-white rounded-xl"
                      data-testid="import-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {importing ? 'Importiere...' : 'Importieren'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </Tabs>
    </div>
  );
}
